import { useState } from 'react';
import { WEEKDAYS } from '@/lib/dates';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AdminPage,
  Button,
  ErrorState,
  Panel,
  Skeleton,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { useModule } from '@/access/useModule';
import { supabase } from '@/lib/supabase';
import { errorMessage } from '@/lib/errors';

/**
 * דרישות כוח אדם (PRD 5.13).
 *
 * כמה אנשים צריך בכל רצועה בכל יום. זה המספר שלוח הסידור מתריע
 * מולו, ובלעדיו האזהרה "חוסר" לא אומרת כלום.
 *
 * מסך ייעודי ולא רשימה גנרית: השורה כאן היא הצטלבות של רצועה ויום,
 * וטבלה של שבעה ימים על פני הרצועות היא הדרך היחידה לראות את זה
 * ולערוך במהירות.
 */


type Requirement = {
  id: string;
  shift_type_id: string;
  weekday: number;
  job_title_id: string | null;
  required_count: number;
};

export function RequirementsPage() {
  const canManage = usePerm('schedule.settings');
  const scheduleOn = useModule('schedule');
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const data = useQuery({
    queryKey: ['shift-requirements'],
    queryFn: async () => {
      const [shifts, requirements] = await Promise.all([
        supabase
          .from('shift_types')
          .select('id, name, start_time, end_time')
          .eq('is_active', true)
          .order('sort_order'),
        supabase.from('shift_requirements').select('*').is('job_title_id', null),
      ]);
      if (shifts.error) throw shifts.error;
      if (requirements.error) throw requirements.error;
      return {
        shifts: shifts.data ?? [],
        requirements: (requirements.data ?? []) as Requirement[],
      };
    },
  });

  const save = useMutation({
    mutationFn: async (input: { shiftTypeId: string; weekday: number; count: number }) => {
      const { error: failed } = await supabase.from('shift_requirements').upsert(
        {
          shift_type_id: input.shiftTypeId,
          weekday: input.weekday,
          job_title_id: null,
          required_count: input.count,
        },
        { onConflict: 'shift_type_id,weekday,job_title_id' },
      );
      if (failed) throw failed;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['shift-requirements'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  if (!canManage || !scheduleOn) return <Navigate to="/admin/settings" replace />;
  if (data.isPending) return <Skeleton className="m-8 h-64" />;
  if (data.isError) return <ErrorState onRetry={() => void data.refetch()} />;

  const { shifts, requirements } = data.data;
  const countFor = (shiftTypeId: string, weekday: number) =>
    requirements.find((r) => r.shift_type_id === shiftTypeId && r.weekday === weekday)
      ?.required_count ?? 0;

  return (
    <AdminPage
      title="דרישות כוח אדם"
      subtitle="כמה אנשים צריך בכל רצועה ובכל יום"
      backTo="/admin/settings"
      width="narrow"
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      <p className="m-0 text-body-sm text-muted">
        המספר הזה הוא מה שלוח הסידור מתריע מולו. אפס פירושו שאין דרישה, ולכן גם לא תהיה
        אזהרת חוסר.
      </p>

      {shifts.map((shift) => (
        <Panel key={shift.id} title={shift.name}>
          <div className="grid gap-2">
            <p className="m-0 text-label tabular text-muted" dir="ltr">
              {shift.start_time?.slice(0, 5)}–{shift.end_time?.slice(0, 5)}
            </p>

            <div className="grid gap-2 sm:grid-cols-4">
              {WEEKDAYS.map((label, weekday) => (
                <div key={weekday} className="grid gap-1">
                  <label
                    htmlFor={`${shift.id}-${weekday}`}
                    className="text-body-sm text-muted"
                  >
                    {label}
                  </label>
                  <input
                    id={`${shift.id}-${weekday}`}
                    type="number"
                    min={0}
                    max={20}
                    defaultValue={countFor(shift.id, weekday)}
                    key={`${shift.id}-${weekday}-${countFor(shift.id, weekday)}`}
                    onBlur={(event) => {
                      const count = Number(event.target.value);
                      if (count === countFor(shift.id, weekday)) return;
                      if (count < 0) return;
                      setError(null);
                      save
                        .mutateAsync({ shiftTypeId: shift.id, weekday, count })
                        .catch((caught) => setError(errorMessage(caught)));
                    }}
                    className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body tabular text-ink"
                  />
                </div>
              ))}
            </div>
          </div>
        </Panel>
      ))}

      <div>
        <Button
          onClick={() => {
            const value = window.prompt('כמה אנשים בכל רצועה ובכל יום?', '2');
            if (value === null) return;
            const count = Number(value);
            if (!Number.isFinite(count) || count < 0) return;
            setError(null);
            Promise.all(
              shifts.flatMap((shift) =>
                WEEKDAYS.map((_, weekday) =>
                  save.mutateAsync({ shiftTypeId: shift.id, weekday, count }),
                ),
              ),
            ).catch((caught) => setError(errorMessage(caught)));
          }}
        >
          למלא את הכל באותו מספר
        </Button>
      </div>
    </AdminPage>
  );
}
